import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FireIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface Forum {
  id: string;
  name: string;
  description?: string;
  slug: string;
  isPublic: boolean;
  categories: Category[];
  creatorProfile: {
    user: {
      username: string;
      firstName: string;
      lastName: string;
      avatar?: string;
    };
  };
}

interface Category {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  _count: {
    topics: number;
  };
}

interface RecentTopic {
  id: string;
  title: string;
  views: number;
  createdAt: string;
  author: {
    username: string;
    avatar?: string;
  };
  category: {
    name: string;
    forum: {
      name: string;
      slug: string;
    };
  };
  _count: {
    posts: number;
  };
}

const ForumListPage: React.FC = () => {
  const [forums, setForums] = useState<Forum[]>([]);
  const [recentTopics, setRecentTopics] = useState<RecentTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchForums();
    fetchRecentTopics();
  }, []);

  const fetchForums = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/forums');
      setForums(response.data);
    } catch (error) {
      console.error('Error fetching forums:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentTopics = async () => {
    try {
      const response = await axios.get('/api/forums/search', {
        params: { query: '', limit: 5 },
      });
      setRecentTopics(response.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching recent topics:', error);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/forums/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-600" />
            Community Forums
          </h1>
          <p className="mt-2 text-gray-600">
            Connect, discuss, and share with the community
          </p>
        </div>
        <button
          onClick={() => navigate('/forums/create')}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Create Forum
        </button>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search forums, topics, posts..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Forums List */}
        <div className="lg:col-span-2">
          {forums.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No forums yet
              </h3>
              <p className="text-gray-600 mb-6">
                Be the first to create a community forum!
              </p>
              <button
                onClick={() => navigate('/forums/create')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Forum
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {forums.map((forum) => (
                <div
                  key={forum.id}
                  className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">
                          {forum.name}
                        </h2>
                        {forum.description && (
                          <p className="text-gray-600 mb-3">
                            {forum.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <img
                            src={
                              forum.creatorProfile.user.avatar ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                forum.creatorProfile.user.firstName +
                                  ' ' +
                                  forum.creatorProfile.user.lastName
                              )}`
                            }
                            alt={forum.creatorProfile.user.username}
                            className="h-6 w-6 rounded-full"
                          />
                          <span>
                            by {forum.creatorProfile.user.firstName}{' '}
                            {forum.creatorProfile.user.lastName}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Categories */}
                    {forum.categories.length > 0 && (
                      <div className="border-t border-gray-200 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">
                          Categories
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {forum.categories.map((category) => (
                            <button
                              key={category.id}
                              onClick={() =>
                                navigate(
                                  `/forums/${forum.slug}/categories/${category.id}`
                                )
                              }
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2">
                                {category.icon && (
                                  <span className="text-xl">
                                    {category.icon}
                                  </span>
                                )}
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {category.name}
                                  </div>
                                  {category.description && (
                                    <div className="text-xs text-gray-500">
                                      {category.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-sm text-gray-500">
                                {category._count.topics} topics
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {forum.categories.length === 0 && (
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-sm text-gray-500">
                          No categories yet
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FireIcon className="h-5 w-5 text-orange-500" />
              Recent Activity
            </h3>
            {recentTopics.length > 0 ? (
              <div className="space-y-4">
                {recentTopics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() =>
                      navigate(
                        `/forums/${topic.category.forum.slug}/topics/${topic.id}`
                      )
                    }
                    className="block w-full text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <img
                        src={
                          topic.author.avatar ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            topic.author.username
                          )}`
                        }
                        alt={topic.author.username}
                        className="h-8 w-8 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {topic.title}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                          <span>{topic.category.name}</span>
                          <span>•</span>
                          <span>{topic._count.posts} replies</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No recent activity</p>
            )}
          </div>

          {/* Forum Stats */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Community Stats
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Forums</span>
                <span className="text-lg font-bold text-blue-600">
                  {forums.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Categories</span>
                <span className="text-lg font-bold text-purple-600">
                  {forums.reduce(
                    (sum, forum) => sum + forum.categories.length,
                    0
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Active Topics</span>
                <span className="text-lg font-bold text-green-600">
                  {forums.reduce(
                    (sum, forum) =>
                      sum +
                      forum.categories.reduce(
                        (catSum, cat) => catSum + cat._count.topics,
                        0
                      ),
                    0
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Links
            </h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/forums/my-topics')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                My Topics
              </button>
              <button
                onClick={() => navigate('/forums/my-posts')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                My Posts
              </button>
              <button
                onClick={() => navigate('/forums/saved')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Saved Topics
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForumListPage;
